import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock, ArrowRight, TrendingUp, Check, Sparkles } from "lucide-react";
import confetti from "canvas-confetti";
import { api, apiError, track } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { GuidanceNote } from "@/components/Trust";

function FullRoadmap({ rm, onChange, onRegenerated, navigate }) {
  const [busy, setBusy] = useState("");
  const [regen, setRegen] = useState(false);
  const regenerate = async () => {
    setRegen(true);
    try {
      const { data } = await api.post("/roadmap/regenerate");
      toast.success(data.carried_over ? `New roadmap for ${data.career} · ${data.carried_over} matching task${data.carried_over === 1 ? "" : "s"} carried over` : `New roadmap for ${data.career} · your ${data.previously_done} completed task${data.previously_done === 1 ? "" : "s"} & XP are kept in history`);
      onRegenerated(data);
    } catch (e) { toast.error(apiError(e)); } finally { setRegen(false); }
  };
  const done = new Set(rm.completed_tasks || []);
  const toggle = async (pi, ti) => {
    const id = `${pi}-${ti}`; setBusy(id);
    try {
      const { data } = await api.post("/roadmap/task/toggle", { phase: pi, task: ti });
      if (data.done && data.xp_earned) { toast.success(`+${data.xp_earned} XP · Readiness ${data.readiness}%`); if (data.tasks_done === data.tasks_total) confetti({ particleCount: 160, spread: 90 }); }
      onChange(data.completed_tasks);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const pct = rm.tasks_total ? Math.round((done.size / rm.tasks_total) * 100) : 0;
  return (
    <div className="mt-6 space-y-4" data-testid="full-roadmap">
      {rm.stale && (
        <div className="rounded-3xl border border-amber-300 bg-amber-50 p-5" data-testid="roadmap-stale-banner">
          <div className="font-display font-bold text-amber-900">Your target changed to {rm.current_target}</div>
          <p className="mt-1 text-sm text-amber-800/80">This roadmap was built for {rm.career}. Regenerate it — tasks you've already completed carry over.</p>
          <button onClick={regenerate} disabled={regen} data-testid="roadmap-regenerate-btn" className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-slate-900 py-3 text-sm font-bold text-white hover:bg-indigo-600 disabled:opacity-60">
            {regen ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {regen ? "Rebuilding your roadmap…" : "Regenerate roadmap"}
          </button>
        </div>
      )}
      <div className="rounded-3xl border border-border bg-card p-5">
        <div className="flex items-end justify-between"><div><div className="text-[11px] font-bold uppercase tracking-[0.2em] text-indigo-600">90-day roadmap</div><p className="mt-1 text-sm text-muted-foreground">{rm.roadmap.summary}</p></div><div className="font-mono text-2xl font-extrabold text-indigo-600" data-testid="roadmap-progress">{pct}%</div></div>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-secondary"><motion.div className="h-full rounded-full bg-indigo-600" animate={{ width: `${pct}%` }} /></div>
        <div className="mt-1 text-xs text-muted-foreground">{done.size} / {rm.tasks_total} tasks · +30 XP each · feeds your Career Readiness</div>
      </div>
      {rm.roadmap.phases.map((p, pi) => {
        const phaseDone = p.tasks.every((_, ti) => done.has(`${pi}-${ti}`));
        return (
          <div key={pi} className={`rounded-3xl border p-5 ${phaseDone ? "border-emerald-200 bg-emerald-50/50" : "border-border bg-card"}`} data-testid={`roadmap-phase-${pi}`}>
            <div className="flex items-center justify-between"><div className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">{p.weeks}</div>{phaseDone && <span className="rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-bold text-white">Phase complete</span>}</div>
            <h3 className="mt-1 font-display text-xl font-bold">{p.title}</h3>
            <p className="text-sm text-muted-foreground">{p.focus}</p>
            <ul className="mt-3 space-y-2">
              {p.tasks.map((task, ti) => {
                const id = `${pi}-${ti}`; const isDone = done.has(id);
                return (
                  <li key={id}>
                    <button onClick={() => toggle(pi, ti)} disabled={busy === id} data-testid={`roadmap-task-${id}`} aria-pressed={isDone}
                      className={`flex w-full items-start gap-3 rounded-2xl border p-3 text-left transition-all ${isDone ? "border-emerald-200 bg-white" : "border-border bg-background hover:border-indigo-300"}`}>
                      <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 ${isDone ? "border-emerald-500 bg-emerald-500 text-white" : "border-border"}`}>{busy === id ? <Loader2 className="h-3 w-3 animate-spin" /> : isDone && <Check className="h-3.5 w-3.5" />}</span>
                      <span className={`text-sm font-semibold ${isDone ? "text-muted-foreground line-through" : ""}`}>{task}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
      {rm.history?.length > 0 && (
        <details className="rounded-3xl border border-border bg-card p-5" data-testid="roadmap-history">
          <summary className="cursor-pointer text-sm font-bold">Completed work from earlier roadmaps · {rm.history.reduce((n, h) => n + h.completed.length, 0)} tasks (XP kept)</summary>
          <div className="mt-3 space-y-3">
            {rm.history.map((h, i) => (
              <div key={i}><div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{h.career}</div>
                <ul className="mt-1 space-y-1">{h.completed.map((c, j) => <li key={j} className="flex items-center gap-2 text-sm text-muted-foreground"><Check className="h-3.5 w-3.5 text-emerald-500" /> {c}</li>)}</ul></div>
            ))}
          </div>
        </details>
      )}
      {rm.roadmap.milestone && <div className="rounded-3xl bg-slate-900 p-5 text-white"><div className="flex items-center gap-2 text-amber-300"><Sparkles className="h-4 w-4" /><span className="text-[11px] font-bold uppercase tracking-[0.2em]">Day 90 milestone</span></div><p className="mt-2 font-display text-lg font-bold">{rm.roadmap.milestone}</p></div>}
      <button onClick={() => navigate("/app/home")} data-testid="roadmap-back-home" className="w-full rounded-full bg-slate-900 py-3.5 font-bold text-white hover:bg-indigo-600">Back to Career OS</button>
      {!rm.stale && <button onClick={regenerate} disabled={regen} data-testid="roadmap-regenerate-secondary" className="w-full py-2 text-center text-xs font-semibold text-muted-foreground hover:text-foreground">{regen ? "Rebuilding…" : "Regenerate roadmap (completed tasks carry over)"}</button>}
    </div>
  );
}

export default function Roadmap() {
  const [t, setT] = useState(null);
  const [rm, setRm] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPlus = user?.plan === "plus" || ["admin", "super_admin"].includes(user?.role);

  useEffect(() => {
    track("roadmap_viewed");
    api.get("/roadmap/teaser").then(({ data }) => setT(data)).catch(() => navigate("/result"));
    if (isPlus) {
      api.get("/roadmap").then(async ({ data }) => {
        if (data.exists) setRm(data);
        else { const g = await api.post("/roadmap/generate"); setRm({ ...g.data, exists: true, completed_tasks: [], tasks_total: g.data.roadmap.phases.reduce((n, p) => n + p.tasks.length, 0) }); }
      }).catch(() => {});
    }
  }, [navigate, isPlus]);

  if (!t) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>;

  return (
    <div className="min-h-screen bg-background pb-28">
      <div className="mx-auto max-w-xl px-5 py-6">
        <Logo className="mb-6" />
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600">Your career roadmap</div>
          <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight">
            You could become a<br /><span className="text-indigo-600">{t.target_career}</span>
          </h1>
          <GuidanceNote className="mt-2" />
        </motion.div>

        <div className="mt-6 grid grid-cols-3 gap-3">
          {[["Education", t.profile.education || "—"], ["Experience", t.profile.experience || "—"], ["Target", t.salary_mid]].map(([k, v]) => (
            <div key={k} className="rounded-2xl border border-border bg-card p-4 text-center">
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{k}</div>
              <div className="mt-1 text-sm font-bold">{v}</div>
            </div>
          ))}
        </div>

        <div className="mt-6 rounded-3xl border border-border bg-card p-6">
          <div className="mb-4 flex items-center gap-2"><TrendingUp className="h-5 w-5 text-indigo-600" /><h2 className="font-display text-lg font-bold">Your skill gap</h2></div>
          <div className="space-y-4">
            {t.skill_gaps.map((g, i) => (
              <div key={g.dimension} data-testid={`gap-${g.dimension}`}>
                <div className="mb-1 flex justify-between text-sm">
                  <span className="font-semibold">{g.label}</span>
                  <span className="font-mono font-bold text-indigo-600">{g.current}%</span>
                </div>
                <div className="h-2.5 overflow-hidden rounded-full bg-secondary">
                  <motion.div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500"
                    initial={{ width: 0 }} animate={{ width: `${g.current}%` }} transition={{ duration: 1, delay: i * 0.1 }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {isPlus && rm && <FullRoadmap rm={rm} navigate={navigate} onChange={(ct) => setRm({ ...rm, completed_tasks: ct })}
          onRegenerated={(d) => setRm({ ...rm, ...d, stale: false, current_target: d.career, tasks_done: d.completed_tasks.length })} />}
        {isPlus && !rm && <div className="mt-6 flex items-center justify-center gap-2 rounded-3xl border border-border p-8 text-sm text-muted-foreground" data-testid="roadmap-generating"><Loader2 className="h-4 w-4 animate-spin" /> Building your personalised roadmap…</div>}

        {/* locked detailed roadmap (free) */}
        {!isPlus && (
        <div className="relative mt-6 overflow-hidden rounded-3xl border border-border">
          <div className="select-none blur-[6px]">
            <div className="space-y-3 p-6">
              {["Weeks 1-4: Foundation", "Weeks 5-8: Applied Skills", "Weeks 9-12: Portfolio & Interview"].map((p) => (
                <div key={p} className="rounded-2xl bg-secondary p-4">
                  <div className="font-bold">{p}</div>
                  <div className="mt-1 h-2 w-3/4 rounded bg-border" />
                  <div className="mt-2 h-2 w-1/2 rounded bg-border" />
                </div>
              ))}
            </div>
          </div>
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-gradient-to-b from-background/50 to-background p-6 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-600/10"><Lock className="h-6 w-6 text-indigo-600" /></div>
            <p className="font-display text-lg font-bold">Your personalised 90-day roadmap</p>
            <button onClick={() => navigate("/paywall")} data-testid="build-roadmap-btn"
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-3.5 font-bold text-white transition-colors hover:bg-indigo-600">
              Build My Career Roadmap <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
        )}

        {!isPlus && (
        <button onClick={() => navigate("/app/home")} data-testid="roadmap-continue-free-btn"
          className="mt-6 w-full text-center text-sm font-semibold text-muted-foreground hover:text-foreground">
          Continue with Free
        </button>
        )}
      </div>
    </div>
  );
}
